import React from 'react';
import {Link} from 'react-router-dom';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const {FiPhone,FiMail,FiMapPin,FiGlobe}=FiIcons;

const Footer=()=> {
  const currentYear=new Date().getFullYear();

  const quickLinks=[
    {name: 'Accueil',path: '/'},
    {name: 'À Propos',path: '/about'},
    {name: 'Services',path: '/services'},
    {name: 'Équipe',path: '/team'},
    {name: 'Contact',path: '/contact'}
  ];

  // Mise à jour : Liste des services avec leurs liens correspondants
  const services=[
    {name: 'Formation Professionnelle',path: '/services/formation'},
    {name: 'Audit et Évaluation',path: '/services/audit'},
    {name: 'Conseil Stratégique',path: '/services/conseil'},
    {name: 'Certification ISO',path: '/services/connexes'} // Lien vers Services Connexes qui inclut l'assistance certification
  ];

  const contactInfo=[
    {icon: FiPhone,text: '+237 699 187 929',href: 'tel:+237699187929'},
    {icon: FiMail,text: 'contact@mam-consulting.biz',href: 'mailto:contact@mam-consulting.biz'},
    {icon: FiMapPin,text: 'Douala,Cameroun'},
    {icon: FiGlobe,text: 'mam-consulting.biz',href: 'https://mam-consulting.biz'}
  ];

  const legalLinks=[
    {name: 'Mentions Légales',path: '/legal'},
    {name: 'Politique de Confidentialité',path: '/privacy'},
    {name: 'Conditions d\'utilisation',path: '/terms'}
  ];

  return (
    <footer className="bg-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <motion.div
            initial={{y: 20,opacity: 0}}
            whileInView={{y: 0,opacity: 1}}
            viewport={{once: true}}
          >
            <div className="mb-6">
              <Link to="/">
                <img
                  src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1768751820608-logo%20MAM.jpg"
                  alt="M.A.M Consulting"
                  className="h-24 w-auto object-contain bg-white p-2 rounded-lg mb-4"
                />
              </Link>
            </div>
            <p className="text-gray-300 mb-4">
              Votre partenaire de confiance en développement de compétences et maîtrise des risques.
            </p>
            <p className="text-sm text-gray-400">
              Excellence • Innovation • Confiance
            </p>
          </motion.div>

          {/* Quick Links */}
          <motion.div
            initial={{y: 20,opacity: 0}}
            whileInView={{y: 0,opacity: 1}}
            viewport={{once: true}}
            transition={{delay: 0.1}}
          >
            <h3 className="text-lg font-bold mb-4">Liens Rapides</h3>
            <ul className="space-y-2">
              {quickLinks.map((link,index)=> (
                <li key={index}>
                  <Link
                    to={link.path}
                    className="text-gray-300 hover:text-cyan-400 transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Services - Updated with Links */}
          <motion.div
            initial={{y: 20,opacity: 0}}
            whileInView={{y: 0,opacity: 1}}
            viewport={{once: true}}
            transition={{delay: 0.2}}
          >
            <h3 className="text-lg font-bold mb-4">Nos Services</h3>
            <ul className="space-y-2">
              {services.map((service,index)=> (
                <li key={index}>
                  <Link
                    to={service.path}
                    className="text-gray-300 hover:text-cyan-400 transition-colors text-sm"
                  >
                    {service.name}
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Contact */}
          <motion.div
            initial={{y: 20,opacity: 0}}
            whileInView={{y: 0,opacity: 1}}
            viewport={{once: true}}
            transition={{delay: 0.3}}
          >
            <h3 className="text-lg font-bold mb-4">Contact</h3>
            <ul className="space-y-3">
              {contactInfo.map((info,index)=> (
                <li key={index} className="flex items-center space-x-3">
                  <SafeIcon icon={info.icon} className="w-4 h-4 text-cyan-400 flex-shrink-0" />
                  {info.href ? (
                    <a
                      href={info.href}
                      className="text-gray-300 hover:text-cyan-400 transition-colors text-sm"
                    >
                      {info.text}
                    </a>
                  ) : (
                    <span className="text-gray-300 text-sm">{info.text}</span>
                  )}
                </li>
              ))}
            </ul>
          </motion.div>
        </div>

        {/* Legal Links */}
        <motion.div
          initial={{opacity: 0}}
          whileInView={{opacity: 1}}
          viewport={{once: true}}
          transition={{delay: 0.4}}
          className="border-t border-gray-800 mt-12 pt-8 mb-8"
        >
          <div className="flex flex-col md:flex-row justify-center items-center gap-6 mb-8">
            {legalLinks.map((link,index)=> (
              <React.Fragment key={index}>
                <Link
                  to={link.path}
                  className="text-gray-300 hover:text-cyan-400 transition-colors text-sm"
                >
                  {link.name}
                </Link>
                {index < legalLinks.length - 1 && (
                  <span className="hidden md:block w-1 h-1 bg-gray-600 rounded-full"></span>
                )}
              </React.Fragment>
            ))}
          </div>
        </motion.div>

        {/* Bottom Bar */}
        <motion.div
          initial={{opacity: 0}}
          whileInView={{opacity: 1}}
          viewport={{once: true}}
          transition={{delay: 0.5}}
          className="border-t border-gray-800 pt-8"
        >
          <div className="flex flex-col items-center gap-4 mb-6">
            <div className="flex flex-col md:flex-row items-center gap-4 text-sm text-gray-400 text-center md:text-left">
              <p>© {currentYear} M.A.M Consulting. Tous droits réservés.</p>
              <div className="hidden md:block w-1 h-1 bg-gray-600 rounded-full"></div>
              <div className="flex flex-col md:flex-row space-x-0 md:space-x-4 text-xs gap-2 md:gap-0">
                <span>RCCM: CM-DLA-2024-01-A02710-10</span>
                <span className="hidden md:block">|</span>
                <span>NIU: P098414427388S</span>
              </div>
            </div>
          </div>
          <motion.div className="text-center border-t border-gray-800 pt-6">
            <p className="text-sm text-gray-400">
              Designed with{' '}
              <span className="text-red-500 inline-block mx-1">❤</span>{' '}
              by{' '}
              <a
                href="https://netvisible.net"
                target="_blank"
                rel="noopener noreferrer"
                className="text-cyan-400 hover:text-cyan-300 transition-colors font-medium"
              >
                netvisible.net
              </a>
            </p>
          </motion.div>
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer;